import { useRecoilValue } from 'recoil';
import { currentDatabaseSelector, selectedLockYearState, selectedStateHighwaysSubnetworkState } from '@transport-insights/uikit';
import { useQuery } from 'react-query';
import {
  calculateColourValuesThree,
  convertAreaLineGraph,
  convertLineGraph,
  convertLineGraphFilter,
  returnSingleValue,
  calculateColourValues,
} from '@src/modules/performance/shared/reportCalculations';
import {
  calculateAverageYearRange,
  calculateCoinvestInvestmentKPI,
  calculateCoinvestTechnicalKPI, calculateDeliveryKPI,
  calculateNetworkCharacteristicsKPI,
  calculatePublicTransportKPI,
  calculateRoadNetworkUseKPI,
  calculateTerritorialActivityKPI,
} from '@src/modules/performance/shared/kpi_calcs';
import { convertToFinancialYear } from '@src/utils/dates';
import usePerformanceReportsApi from '../api/usePerformanceReportsApi';
import { selectedRegionRoadTypeState, selectedTemporaryRegionRoadTypeState } from '../state';

// these reports are locked to this lockyear for now
// The dash is required for the backend
export const LOCKYEAR = 'latest';

export function useServicePerformanceKPI() {
  const rca = useRecoilValue(currentDatabaseSelector);
  const api = usePerformanceReportsApi();
  const selectedRegionRoadType = useRecoilValue(selectedRegionRoadTypeState);
  const selectedTemporaryRegionRoadType = useRecoilValue(selectedTemporaryRegionRoadTypeState);
  return useQuery(
    ['service-performance-kpi', rca?.id, selectedTemporaryRegionRoadType || selectedRegionRoadType],
    async () => api.getServicePerformanceKPI(rca?.id, LOCKYEAR),
    {
      enabled: rca?.id !== undefined,
    },
  );
}

export function useAnnualReports() {
  const rca = useRecoilValue(currentDatabaseSelector);
  const api = usePerformanceReportsApi();
  return useQuery(
    ['annual-reports', rca?.id],
    async () => api.getAnnualReports(rca?.id),
    {
      enabled: rca?.id !== undefined,
    },
  );
}

export function useActivityManagementPlans() {
  const rca = useRecoilValue(currentDatabaseSelector);
  const api = usePerformanceReportsApi();
  return useQuery(
    ['activity-management-plans', rca?.id],
    async () => api.getActivityManagementPlans(rca?.id),
    {
      enabled: rca?.id !== undefined,
      // don't retry on unauthorized
      retry: (failureCount, error) => error?.response?.status !== 403,
    },
  );
}

export function useMiscActivityManagementPlans() {
  const rca = useRecoilValue(currentDatabaseSelector);
  const api = usePerformanceReportsApi();
  return useQuery(
    ['activity-management-plans-misc', rca?.id],
    async () => api.getMiscActivityManagementPlans(rca?.id),
    {
      enabled: rca?.id !== undefined,
      // don't retry on unauthorized
      retry: (failureCount, error) => error?.response?.status !== 403,
    },
  );
}

export function useReport(name, processor, framework = null) {
  const rca = useRecoilValue(currentDatabaseSelector);
  const api = usePerformanceReportsApi();
  const selectedSubnetwork = useRecoilValue(selectedStateHighwaysSubnetworkState);
  const selectedRegionRoadType = useRecoilValue(selectedRegionRoadTypeState);
  const selectedTemporaryRegionRoadType = useRecoilValue(selectedTemporaryRegionRoadTypeState);
  return useQuery(
    [name, rca?.id, framework, selectedSubnetwork, selectedTemporaryRegionRoadType || selectedRegionRoadType],
    async () => api.getReport(name, rca?.id, LOCKYEAR, framework),
    {
      enabled: rca?.id !== undefined,
      select: (data) => (processor ? processor(data) : data),
    },
  );
}

export function useReportWithLockYear(name, processor) {
  const rca = useRecoilValue(currentDatabaseSelector);
  const lockYear = useRecoilValue(selectedLockYearState);
  const api = usePerformanceReportsApi();
  const encodedLockYear = lockYear?.replace('/', '-') || null;
  return useQuery(
    [name, rca?.id, lockYear],
    async () => api.getReport(name, rca?.id, encodedLockYear),
    {
      enabled: rca?.id !== undefined && lockYear !== null,
      select: (data) => (processor ? processor(data) : data),
    },
  );
}

// Service Performance Hooks
export function useServicePerformance() {
  return useReport(
    'service-performance/service-performance',
    (res) => res,
  );
}

// Network Characteristics Hooks
export function useNetworkCharacteristicsKPI() {
  return useReport(
    'network-characteristics/network-characteristics-kpi',
    (res) => ({ ...calculateNetworkCharacteristicsKPI(res), lockYear: res[0]?.LockYear || null, averageYearRange: calculateAverageYearRange(res[0]?.LockYear || null, 10) }),
  );
}

export function useRoadsNetworkLength() {
  return useReport(
    'network-characteristics/roads-network-length',
    (res) => res,
  );
}

export function useRoadsUrbanRuralLength() {
  return useReport(
    'network-characteristics/roads-urban-rural-length',
    (res) => res,
  );
}

export function useRoadsUrbanLength() {
  return useReport(
    'network-characteristics/roads-urban-length',
    (res) => res,
  );
}

// Road Network Use Hooks
export function useRoadNetworkUseKPI() {
  return useReport(
    'road-network-use/road-network-use-kpi',
    (res) => ({ ...calculateRoadNetworkUseKPI(res), lockYear: res[0]?.LockYear || null, averageYearRange: calculateAverageYearRange(res[0]?.LockYear || null, 10) }),
  );
}

// Public Transport Hooks
export function usePublicTransportKPI() {
  return useReport(
    'public-transport/public-transport-kpi',
    (res) => ({ ...calculatePublicTransportKPI(res), lockYear: res[0]?.LockYear || null, averageYearRange: calculateAverageYearRange(res[0]?.LockYear || null, 5) }),
  );
}

export function usePassengerKms() {
  return useReport(
    'public-transport/passenger-kms',
    (res) => res,
  );
}

export function useNoBuses() {
  return useReport(
    'public-transport/no-buses',
    (res) => res,
  );
}

export function useServiceKms() {
  return useReport(
    'public-transport/service-kms',
    (res) => res,
  );
}

export function usePopulationServicedCommunity() {
  return useReport(
    'public-transport/population-serviced-community',
    (res) => res,
  );
}

export function usePopulationBusStop() {
  return useReport(
    'public-transport/population-busstop',
    (res) => res,
  );
}

export function useBoardings() {
  return useReport(
    'public-transport/boardings',
    (res) => res,
  );
}

export function useServiceExpenditure() {
  return useReport(
    'public-transport/service-expenditure',
    (res) => res,
  );
}

export function useInfrastructureExpenditure() {
  return useReport(
    'public-transport/infrastructure-expenditure',
    (res) => res,
  );
}

export function useServicedCommunities() {
  return useReport('public-transport/serviced-communities');
}

export function useTotalMobilityServicedCommunities() {
  return useReport('public-transport/total-mobility-communities');
}

export function useTotalMobilityUse() {
  return useReport(
    'public-transport/total-mobility-use',
    (res) => res,
  );
}

// Road Network Use Hooks
export function useVKT() {
  return useReport(
    'road-network-use/vkt',
    (res) => res,
  );
}

export function useHeavyVKT() {
  return useReport(
    'road-network-use/vkt-heavy',
    (res) => res,
  );
}

export function useHeavyVKTPercentage() {
  return useReport(
    'road-network-use/vkt-heavy-percent',
    (res) => res,
  );
}

export function useTotalTrafficLaneKm() {
  return useReport(
    'road-network-use/vkt-per-lane-km',
    (res) => res,
  );
}
export function useHeavyTrafficLaneKm() {
  return useReport(
    'road-network-use/heavy-vkt-per-lane-km',
    (res) => res,
  );
}

export function useRestrictedBridges() {
  return useReport(
    'road-network-use/restricted-bridges',
    (res) => res,
  );
}

export function useLengthVsVKT(framework) {
  return useReport(
    'road-network-use/length-vs-vkt',
    (res) => res,
    framework,
  );
}

// Network Characteristics Hooks
export function useCyclewaysNetworkLength() {
  return useReport(
    'network-characteristics/cycleways-network-length',
    (res) => res,
  );
}

export function useBridgesCount() {
  return useReport(
    'network-characteristics/bridges-count',
    (res) => res,
  );
}

// Territorial Activity Hooks
export function useTerritorialActivityKPI() {
  return useReport(
    'territorial-activity/territorial-activity-kpi',
    (res) => ({ ...calculateTerritorialActivityKPI(res), lockYear: res[0]?.LockYear || null, averageYearRange: calculateAverageYearRange(res[0]?.LockYear || null, 10) }),
  );
}

export function useResidentPopulation() {
  return useReport(
    'territorial-activity/resident-population',
    (res) => res,
  );
}

export function useGDPByIndustry() {
  return useReport(
    'territorial-activity/gdp-by-industry',
    (res) => res,
  );
}

// Network Availability Hooks
export function useNetworkAvailabilityKPI() {
  return useReport(
    'network-availability/network-availability-kpi',
    (res) => ({ ...returnSingleValue(res, ['HCV_pc', 'FiftyMax_pc'], null, 100), lockYear: res?.LockYear || null }),
  );
}

export function useJourneysUnplannedEvents(framework) {
  return useReport(
    'network-availability/journeys-unplanned-events',
    (res) => res,
    framework,
  );
}

export function useJourneysUnplannedEventsFreight(framework) {
  return useReport(
    'network-availability/journeys-unplanned-events-freight',
    (res) => res,
    framework,
  );
}

export function useJourneysUnplannedEventsGeneralTraffic(framework) {
  return useReport(
    'network-availability/journeys-unplanned-events-gt',
    (res) => res,
    framework,
  );
}

export function useRoadAccessLost(framework) {
  return useReport(
    'network-availability/road-access-lost',
    (res) => res,
    framework,
  );
}

export function useRoadAccessLostFreight(framework) {
  return useReport(
    'network-availability/road-access-lost-freight',
    (res) => res,
    framework,
  );
}

export function useRoadAccessLostGeneralTraffic(framework) {
  return useReport(
    'network-availability/road-access-lost-gt',
    (res) => res,
    framework,
  );
}

// We need to convert the value to % by dividing the value by totalKm and then multiplying this value by 100
export function useFreightAccessibility(framework) {
  return useReport(
    'network-availability/freight-accessibility',
    (res) => res,
    framework,
  );
}

export function useFreightAccessibilitySourceYear() {
  return useReport(
    'network-availability/freight-accessibility',
    (res) => res[0].Max_Class_1_Year,
  );
}

// Coinvestor Hooks
export function useInvestmentAudit() {
  return useReport('coinvestor/investment-audit', (res) => res || []);
}

export function useTechnicalAudit() {
  return useReport('coinvestor/technical-audit', (res) => res || []);
}

export function useInvestmentAuditKPI() {
  return useReport('coinvestor/investment-audit-kpi', (res) => ({ ...calculateCoinvestInvestmentKPI(res), date: convertToFinancialYear(res.date) }));
}

export function useTechnicalAuditKPI() {
  return useReport('coinvestor/technical-audit-kpi', (res) => ({ ...calculateCoinvestTechnicalKPI(res), date: convertToFinancialYear(res.date) }));
}

// Delivery Hooks
export function useDeliveryKPI() {
  return useReport('delivery/delivery-kpi', (res) => ({ ...calculateDeliveryKPI(res), lockYear: res[0]?.LockYear || null }));
}

export function useAllTransportActivities() {
  return useReport('delivery/all-transport-activities', (res) => res);
}

export function useNewRoadsRoadImprovements() {
  return useReport('delivery/new-roads-road-improvements', (res) => res);
}

export function useRoadMaintenance() {
  return useReport('delivery/road-maintenance', (res) => res);
}

export function useCoInvestExpenditure() {
  return useReport('delivery/co-invest-expenditure', (res) => res || []);
}

export function useRoadSafetyPromotion() {
  return useReport('delivery/road-safety-promotion', (res) => res || []);
}

export function useWalkingCycling() {
  return useReport('delivery/walking-cycling', (res) => res || []);
}

export function useInvestManagement() {
  return useReport('delivery/invest-management', (res) => res || []);
}

export function useRoadingAssets() {
  return useReport('delivery/roading-assets', (res) => res);
}

export function useTotalExpenditure() {
  return useReport('delivery/total-expenditure', (res) => res || []);
}

export function useMaintenanceOperations() {
  return useReport(
    'delivery/maintenance-operations',
    (res) => res || [],
  );
}

export function useMaintenanceCostComparisons() {
  return useReportWithLockYear(
    'maintenance-cost-comparisons',
    (res) => res || [],
  );
}

// Activity Management Hooks
export function usePlanningQuality() {
  return useReport(
    'activity-management/planning-quality',
    (res) => ({ ...calculateColourValuesThree(res, 3.0), lockYear: res?.LockYear || null }),
  );
}

export function useCoinvestmentPlanningQuality() {
  return useReport(
    'activity-management/coinvestment-planning-quality',
    (res) => ({ ...calculateColourValuesThree(res, 3.0), lockYear: res?.LockYear || null }),
  );
}

export function useSmartBuyerSelfAssessment() {
  return useReport(
    'activity-management/smart-buyer-self-assessment',
    (res) => ({
      ...calculateColourValues(res, 70),
      lockYear: convertToFinancialYear(res.source) || res?.LockYear || null,
    }),
  );
}

export function usePlanningQualityPillars() {
  const rca = useRecoilValue(currentDatabaseSelector);
  const api = usePerformanceReportsApi();
  return useQuery(
    ['activity-management/planning-quality-pillars', rca?.id],
    async () => api.getActivityManagement('planning-quality-pillars', rca?.id, LOCKYEAR),
    {
      enabled: rca?.id !== undefined,
    },
  );
}

export function usePlanningQualityTrend() {
  return useReport(
    'activity-management/planning-quality-trend',
    (res) => res || [],
  );
}

// Achievement Hooks
export function usePavementRehabilitation() {
  return useReport('achievements/pavement-rehabilitation', (res) => res || []);
}

export function usePavementResurfacing() {
  return useReport('achievements/pavement-resurfacing', (res) => res || []);
}

export function usePavementResurfacingAsphalt() {
  return useReport('achievements/pavement-resurfacing-asphalt', (res) => res || []);
}

export function usePavementResurfacingChipseal() {
  return useReport('achievements/pavement-resurfacing-chip-seal', (res) => res || []);
}

export function usePercentageNetworkRenewed() {
  return useReport('achievements/percentage-network-renewed', (res) => ({ ...convertLineGraphFilter(res, 'Percentage_of_Sealed_Kms', 'Type', ['Rehab', 'Resurface'], 100), lockYear: res[0]?.LockYear || null }));
}

export function useRideQualityRoughness() {
  return useReport('achievements/ride-quality-roughness', (res) => ({ ...convertAreaLineGraph(res, 'Value', 'PeerGroupValue', null, 100), lockYear: res[0]?.LockYear || null }));
}

export function useUnsealedRoadMetalling() {
  return useReport('achievements/unsealed-road-metalling', (res) => res || {});
}

export function useNewAndImprovedRoads() {
  return useReport('achievements/new-and-improved-roads', (res) => res || {});
}

export function useBridgeRenewalsAndNewBridges() {
  return useReport('achievements/bridge-renewals-and-new-bridges', (res) => res || {});
}

export function useCulvertDrainageRenewals() {
  return useReport('achievements/drainage-renewals-culverts', (res) => res || {});
}

export function useKerbAndChannelDrainageRenewals() {
  return useReport('achievements/drainage-renewals-kerb-and-channel', (res) => res || {});
}

export function useOtherDrainageRenewals() {
  return useReport('achievements/drainage-renewals-other', (res) => res || {});
}

export function useRoadMaintenanceCost() {
  return useReport('achievements/road-maintenance-cost', (res) => res || {});
}

export function useQuartelyRoadMaintenanceCost() {
  return useReport('achievements/quarterly-road-maintenance-cost', (res) => res || {});
}

export function usePeakAverageRoadRoughness() {
  return useReport(
    'achievements/peak-average-road-roughness',
    (res) => ({ ...convertLineGraph(res, ['perc75_net', 'PG_perc75_net', 'perc85_net', 'PG_perc85_net', 'naasra_avg_net', 'PG_naasra_avg', 'National_Average']), lockYear: res[0]?.LockYear || null }),
  );
}

export function usePavementCondition() {
  return useReport(
    'achievements/pavement-condition',
    (res) => res,
  );
}

export function useSurfaceCondition() {
  return useReport(
    'achievements/surface-condition',
    (res) => res,
  );
}

export function useCcdcCoverage() {
  return useReport(
    'achievements/ccdc-coverage',
    (res) => res,
  );
}

// Road Safety Hooks
export function useTotalPopulation() {
  return useReport(
    'safety/total-population',
    (res) => res || [],
  );
}

export function useTotalVKTPersonalRisk() {
  return useReport(
    'safety/total-vkt-personal-risk',
    (res) => res || [],
  );
}

export function useTotalCollectiveRisk() {
  return useReport(
    'safety/total-collective-risk',
    (res) => res || [],
  );
}

export function useRoadMode() {
  return useReport(
    'safety/road-mode',
    (res) => res || [],
  );
}

export function useWalkingMode() {
  return useReport(
    'safety/walking-mode',
    (res) => res || [],
  );
}

export function useCyclingMode() {
  return useReport(
    'safety/cycling-mode',
    (res) => res || [],
  );
}

export function useCrashDistributionStreetCategories() {
  return useReport(
    'safety/crash-distribution',
    (res) => res,
  );
}

export function useCrashDistributionFreight() {
  return useReport(
    'safety/crash-distribution-freight',
    (res) => res,
  );
}

export function useCrashDistributionGeneralTraffic() {
  return useReport(
    'safety/crash-distribution-gt',
    (res) => res,
  );
}

export function useCrashDistributionOnrc() {
  const framework = 'onrc';
  return useReport(
    'safety/crash-distribution',
    (res) => res,
    framework,
  );
}

export function useDsi() {
  return useReport(
    'safety/map-kpi',
    (res) => res || [],
  );
}

export function useCrashConditionWet() {
  return useReport(
    'safety/wet-mode',
    (res) => res || [],
  );
}

export function useCrashConditionNight() {
  return useReport(
    'safety/night-mode',
    (res) => res || [],
  );
}

export function useCrashConditionIntersection() {
  return useReport(
    'safety/intersection-mode',
    (res) => res || [],
  );
}

export function useHeavyDsi() {
  return useReport(
    'safety/heavy-mode',
    (res) => res || [],
  );
}

export function useInfrastructureRiskRating() {
  return useReport(
    'safety/irr',
    (res) => res,
  );
}

// Stats Hooks
export function useStats() {
  return useReport('stats/stats');
}

// Road Maintenance

export function useRoadMaintenanceKPI() {
  return useReport('road-maintenance/kpi', (res) => res || {});
}

// E3 Report Hooks

export function useE3Kpi() {
  const rca = useRecoilValue(currentDatabaseSelector);
  const api = usePerformanceReportsApi();
  const selectedSubnetwork = useRecoilValue(selectedStateHighwaysSubnetworkState);
  const selectedRegionRoadType = useRecoilValue(selectedRegionRoadTypeState);
  const selectedTemporaryRegionRoadType = useRecoilValue(selectedTemporaryRegionRoadTypeState);
  return useQuery(
    ['e3-kpi', rca?.id, selectedTemporaryRegionRoadType || selectedRegionRoadType, selectedSubnetwork],
    async () => api.getKpi('e3', rca?.id),
    {
      enabled: rca?.id !== undefined,
    },
  );
}

export function useE3HistoricalData() {
  return useReport(
    'e3/historical',
    (res) => res || [],
  );
}

export function useE3PeerGroups() {
  return useReport('e3/peer-group', (res) => res || []);
}

// Pothole Repairs Hooks

export function usePotholeRepairsKPI() {
  const rca = useRecoilValue(currentDatabaseSelector);
  const api = usePerformanceReportsApi();
  const selectedSubnetwork = useRecoilValue(selectedStateHighwaysSubnetworkState);
  const selectedRegionRoadType = useRecoilValue(selectedRegionRoadTypeState);
  const selectedTemporaryRegionRoadType = useRecoilValue(selectedTemporaryRegionRoadTypeState);

  return useQuery(
    ['pothole-repairs-kpi', rca?.id, selectedTemporaryRegionRoadType || selectedRegionRoadType, selectedSubnetwork],
    () => api.getKpi('potholes', rca?.id),
    {
      enabled: rca?.id !== undefined && rca?.id !== 81,
    },
  );
}

// Data Quality Hooks
export function useDataQualityKPI() {
  return useReport(
    'data-quality/kpi',
    (res) => res || {},
  );
}

export function useDQAssetManagement() {
  return useReport(
    'data-quality/asset-management',
    (res) => res || [],
  );
}

// CCDC Rutting Hooks

export function useRuttingDistribution() {
  return useReport(
    'ccdc-rutting/distribution',
    (res) => res,
  );
}

export function useRuttingStats() {
  return useReport(
    'ccdc-rutting/stats',
    (res) => res,
  );
}

// CCDC Texture Hooks

export function useTextureDistribution() {
  return useReport(
    'ccdc-texture/distribution',
    (res) => res,
  );
}

export function useTextureStats() {
  return useReport(
    'ccdc-texture/stats',
    (res) => res,
  );
}

// Average Life Achieved Hooks

export function useAverageLifeAchievedChipseal() {
  return useReport(
    'achieved-life/chipseal-resurfacing',
    (res) => res,
  );
}

export function useAverageLifeAchievedAsphalt() {
  return useReport(
    'achieved-life/asphalt-resurfacing',
    (res) => res,
  );
}

export function useAverageLifeAchievedPavement() {
  return useReport(
    'achieved-life/pavement-rehabilitation',
    (res) => res,
  );
}

/*
  State Highways
*/

// List of State Highways Subnetworks

export function useStateHighwaysSubnetworks() {
  const api = usePerformanceReportsApi();
  const rca = useRecoilValue(currentDatabaseSelector);

  return useQuery(
    ['state-highway-pothole-subnetworks'],
    () => api.getStateHighwaysSubnetworks(),
    {
      enabled: rca?.id === 81,
    },
  );
}

// State Highways Pothole Repairs Hooks

export function useStateHighwaysPotholeRepairsKPI() {
  const rca = useRecoilValue(currentDatabaseSelector);
  const selectedSubnetwork = useRecoilValue(selectedStateHighwaysSubnetworkState);
  const api = usePerformanceReportsApi();

  return useQuery(
    ['state-highway-pothole-repairs-kpi', selectedSubnetwork],
    () => api.getStateHighwaysData(`state-highways-potholes/kpi/${selectedSubnetwork || ''}`),
    {
      enabled: rca?.id !== undefined && rca?.id === 81,
    },
  );
}

export function useStateHighwaysPotholeRepairsHighVolumeKPI() {
  const rca = useRecoilValue(currentDatabaseSelector);
  const selectedSubnetwork = useRecoilValue(selectedStateHighwaysSubnetworkState);
  const api = usePerformanceReportsApi();

  return useQuery(
    ['state-highway-pothole-repairs-high-volume-kpi', selectedSubnetwork],
    () => api.getStateHighwaysData(`state-highways-potholes/noc-high-volume/${selectedSubnetwork || ''}`),
    {
      enabled: rca?.id !== undefined,
    },
  );
}

export function useStateHighwaysPotholeRepairsRegionalKPI() {
  const rca = useRecoilValue(currentDatabaseSelector);
  const selectedSubnetwork = useRecoilValue(selectedStateHighwaysSubnetworkState);
  const api = usePerformanceReportsApi();

  return useQuery(
    ['state-highway-pothole-repairs-regional-kpi', selectedSubnetwork],
    () => api.getStateHighwaysData(`state-highways-potholes/noc-regional/${selectedSubnetwork || ''}`),
    {
      enabled: rca?.id !== undefined,
    },
  );
}

export function useStateHighwaysPotholeRepairsNocData() {
  const rca = useRecoilValue(currentDatabaseSelector);
  const selectedSubnetwork = useRecoilValue(selectedStateHighwaysSubnetworkState);
  const api = usePerformanceReportsApi();

  return useQuery(
    ['state-highway-pothole-repairs-noc-data', selectedSubnetwork],
    () => api.getStateHighwaysData('state-highways-potholes/noc'),
    {
      enabled: rca?.id !== undefined,
    },
  );
}

export function useStateHighwaysPotholeRepairsHnoData() {
  const rca = useRecoilValue(currentDatabaseSelector);
  const selectedSubnetwork = useRecoilValue(selectedStateHighwaysSubnetworkState);
  const api = usePerformanceReportsApi();

  return useQuery(
    ['state-highway-pothole-repairs-hno-data', selectedSubnetwork],
    () => api.getStateHighwaysData('state-highways-potholes/hno'),
    {
      enabled: rca?.id !== undefined,
    },
  );
}
